import { injectAesthetic } from '../../../../components/library/ui/shared-styles.ts';
import '../../../../components/library/ui/app-icon.ts';
import '../../../../components/library/ui/app-button.ts';
import '../../../../components/library/ui/app-modal.ts';
import { OrganizationService } from '../../../../services/organization.ts';
import { ApiClient } from '../../../../services/api.ts';

export class GatewayNodesTab extends HTMLElement {
  private _orgId: string = '';
  private _hasByoc: boolean = false;
  private _hasDedicated: boolean = false;
  private _hasShared: boolean = false;
  private _nodes: any[] = [];
  private _orgService = new OrganizationService();
  private _apiService = ApiClient.getInstance();
  private seed = Math.floor(Math.random() * 10000);
  private _isLoading = false;
  private _systemConfig: any = null;

  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
  }

  static get observedAttributes() {
    return ['org-id', 'has-byoc', 'has-dedicated', 'has-shared'];
  }

  attributeChangedCallback(name: string, oldVal: string, newVal: string) {
    let shouldRender = false;
    if (name === 'org-id' && oldVal !== newVal) {
      this._orgId = newVal;
      if (this.isConnected) {
        this.loadNodes();
      }
    }
    if (name === 'has-byoc' && oldVal !== newVal) {
      this._hasByoc = newVal === 'true';
      shouldRender = true;
    }
    if (name === 'has-dedicated' && oldVal !== newVal) {
      this._hasDedicated = newVal === 'true';
      shouldRender = true;
    }
    if (name === 'has-shared' && oldVal !== newVal) {
      this._hasShared = newVal === 'true';
      shouldRender = true;
    }
    
    if (shouldRender && this.isConnected && !this._isLoading) {
      this.render();
    }
  }

  async connectedCallback() {
    await this.loadNodes();
  }

  private _subscription: any = null;

  private async loadNodes() {
    if (!this._orgId) return;
    this._isLoading = true;
    this.render();

    try {
      const [nodes, configResponse, sub] = await Promise.all([
        this._orgService.getGateways(this._orgId),
        this._apiService.request('/system/config', 'GET').catch(() => ({ data: null })),
        this._orgService.getSubscription(this._orgId).catch(() => null)
      ]);
      this._nodes = nodes;
      this._subscription = sub;
      if (configResponse.data) {
        this._systemConfig = configResponse.data;
      }
    } catch (e) {
      console.error('Failed to load gateway nodes', e);
    } finally {
      this._isLoading = false;
      this.render();
    }
  }

  private async handleProvisionNode() {
    const name = await this.promptForNodeName();
    if (!name) return;

    try {
      const gateway = await this._orgService.createGateway(this._orgId, name, 'private_byoc');
      this.showProvisioningModal(gateway);
      await this.loadNodes();
    } catch (e) {
      // Toast handled by API service
    }
  }

  private promptForNodeName(): Promise<string | null> {
    return new Promise(resolve => {
      const modal = document.createElement('app-modal') as any;
      modal.setAttribute('size', 'md');
      modal.setAttribute('open', '');
      
      modal.innerHTML = `
        <div slot="header">Provision BYOC Node</div>
        <div style="margin-bottom: 24px;">
          <p style="margin-bottom: 12px; color: var(--text-dim); font-size: 0.9rem;">
            Enter a name for the new BYOC Gateway Node.
          </p>
          <input type="text" id="node-name-input" placeholder="e.g., US-East-Datacenter-1" style="width: 100%; padding: 8px 12px; border-radius: 6px; border: 1px solid rgba(255,255,255,0.1); background: rgba(0,0,0,0.2); color: white; outline: none; font-family: var(--font-main); font-size: 0.9rem;">
        </div>
        <div slot="actions" style="display: flex; gap: 8px; justify-content: flex-end; width: 100%;">
          <app-button variant="ghost" id="modal-cancel-btn">Cancel</app-button>
          <app-button variant="primary" id="modal-provision-btn">Provision</app-button>
        </div>
      `;

      document.body.appendChild(modal);

      const input = modal.querySelector('#node-name-input');
      requestAnimationFrame(() => input.focus());

      const closeModal = (result: string | null) => {
        modal.removeAttribute('open');
        setTimeout(() => document.body.removeChild(modal), 300);
        resolve(result);
      };

      modal.querySelector('#modal-cancel-btn').addEventListener('click', () => closeModal(null));
      modal.querySelector('#modal-provision-btn').addEventListener('click', () => {
        closeModal(input.value.trim() || null);
      });
      input.addEventListener('keydown', (e: KeyboardEvent) => {
        if (e.key === 'Enter') closeModal(input.value.trim() || null);
        if (e.key === 'Escape') closeModal(null);
      });
    });
  }

  private async handleRevokeNode(gatewayId: string, name: string) {
    if (!confirm(`Are you sure you want to revoke the token for node "${name}"? It will immediately lose access to the control plane.`)) {
      return;
    }

    try {
      await this._orgService.deleteGateway(this._orgId, gatewayId);
      await this.loadNodes();
    } catch (e) {
      // Toast handled by API service
    }
  }

  private showProvisioningModal(gateway: any) {
    const modal = document.createElement('app-modal') as any;
    modal.setAttribute('size', 'md');
    modal.setAttribute('open', '');
    
    const coreUrl = this._systemConfig?.core_api_url || 'https://api.crate.cc';

    modal.innerHTML = `
      <div slot="header">Node Setup: ${gateway.name}</div>
      <div style="margin-bottom: 24px; color: var(--text-main);">
        <p style="margin-bottom: 24px; color: var(--text-dim); font-size: 0.9rem;">
          To start the gateway on your hardware, run the following Docker command. Make sure to keep the token secret.
        </p>
        <div style="background: rgba(255,255,255,0.03); padding: 16px; border-radius: 8px; border: 1px solid rgba(255,255,255,0.1); font-family: monospace; font-size: 0.85rem; color: #a78bfa; word-break: break-all;">
docker run -d \\<br>
&nbsp;&nbsp;-e CRATE_GATEWAY_TOKEN="${gateway.token || '&lt;Hidden&gt;'}" \\<br>
&nbsp;&nbsp;-e CRATE_CORE_URL="${coreUrl}" \\<br>
&nbsp;&nbsp;cratecc/gateway:latest
        </div>
      </div>
      <div slot="actions">
        <app-button variant="primary" id="modal-close-btn" style="width: 100%;">Done</app-button>
      </div>
    `;

    document.body.appendChild(modal);
    modal.querySelector('#modal-close-btn').addEventListener('click', () => {
      modal.removeAttribute('open');
      setTimeout(() => document.body.removeChild(modal), 300);
    });
  }

  private render() {
    if (!this.shadowRoot) return;

    if (this._isLoading) {
      this.shadowRoot.innerHTML = `
        ${injectAesthetic(this.seed)}
        <div style="padding: 40px; text-align: center; color: var(--text-muted);">
          <div class="loader"></div>
          <style>
            .loader {
              width: 40px; height: 40px;
              border: 3px solid rgba(245, 158, 11, 0.1);
              border-top-color: var(--accent, #f59e0b);
              border-radius: 50%;
              animation: spin 1s linear infinite;
              margin: 0 auto;
            }
            @keyframes spin { to { transform: rotate(360deg); } }
          </style>
        </div>
      `;
      return;
    }

    const byocNodes = this._nodes.filter(n => n.type === 'private_byoc');
    const dedicatedNodes = this._nodes.filter(n => n.type === 'private_managed');

    const byocLimit = this._subscription?.modules?.filter((m: any) => m.id === 'byoc_gateway').length || 0;
    const canProvision = byocNodes.length < byocLimit;
    
    this.shadowRoot.innerHTML = `
      ${injectAesthetic(this.seed)}
      <style>
        :host { 
          display: block; 
          color: white;
          animation: fade-in 0.3s ease-out;
        }

        @keyframes fade-in {
          from { opacity: 0; transform: translateY(4px); }
          to { opacity: 1; transform: translateY(0); }
        }

        .page-header {
          margin-bottom: 40px;
        }

        .header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 24px;
        }

        .table-container { 
          background: rgba(255, 255, 255, 0.01); 
          border-radius: 12px; 
          overflow-x: auto; 
          border: 1px solid rgba(255, 255, 255, 0.05);
        }

        table { 
          width: 100%; 
          border-collapse: collapse; 
          text-align: left; 
        }

        th { 
          padding: 1rem; 
          color: #94a3b8; 
          font-weight: 600; 
          font-size: 0.75rem; 
          text-transform: uppercase;
          letter-spacing: 0.1em;
          border-bottom: 1px solid rgba(255, 255, 255, 0.1); 
        }

        td { 
          padding: 1rem; 
          border-bottom: 1px solid rgba(255, 255, 255, 0.05); 
          font-size: 0.9rem; 
        }

        tr:last-child td {
          border-bottom: none;
        }

        .badge-active {
          background: rgba(16, 185, 129, 0.1);
          color: #10b981;
          padding: 2px 8px;
          border-radius: 12px;
          font-size: 0.75rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.05em;
        }

        .badge-inactive {
          background: rgba(239, 68, 68, 0.1);
          color: #ef4444;
          padding: 2px 8px;
          border-radius: 12px;
          font-size: 0.75rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.05em;
        }

        .badge-provisioning {
          background: rgba(245, 158, 11, 0.1);
          color: #f59e0b;
          padding: 4px 10px;
          border-radius: 16px;
          font-size: 0.75rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          display: inline-flex;
          align-items: center;
          gap: 6px;
          border: 1px solid rgba(245, 158, 11, 0.2);
        }

        .pulse-dot {
          width: 6px;
          height: 6px;
          background-color: #f59e0b;
          border-radius: 50%;
          animation: pulse 1.5s cubic-bezier(0.4, 0, 0.6, 1) infinite;
        }

        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.3; }
        }

        .btn-copy {
          background: none;
          border: none;
          cursor: pointer;
          color: var(--text-dim);
          transition: color 0.2s;
          display: inline-flex;
          align-items: center;
          padding: 2px;
          border-radius: 4px;
        }
        .btn-copy:hover {
          color: var(--text-main);
          background: rgba(255, 255, 255, 0.05);
        }
      </style>

      <div class="page-header">
        <h2 style="margin: 0 0 8px 0; font-family: var(--font-main); font-weight: 500;">Gateway Infrastructure</h2>
        <p style="margin: 0; color: var(--text-dim); font-size: 0.95rem;">Manage and view your organization's API gateway nodes.</p>
      </div>

      ${this._hasShared ? `
        <div style="margin-bottom: 48px;">
          <h3 style="margin: 0 0 12px 0; font-family: var(--font-main); font-weight: 500; font-size: 1.1rem; color: var(--text-main);">Shared Edge Network</h3>
          <p style="margin: 0 0 16px 0; color: var(--text-dim); font-size: 0.9rem;">
            Your API traffic is being routed through Crate's global shared gateway edge network.
          </p>
          <div style="background: rgba(255,255,255,0.02); padding: 16px; border-radius: 8px; border: 1px solid rgba(255,255,255,0.05);">
            <div style="display: flex; align-items: center; gap: 12px;">
              <app-icon name="globe" size="24" style="color: #3b82f6;"></app-icon>
              <div>
                <div style="font-size: 0.85rem; color: var(--text-dim); text-transform: uppercase; letter-spacing: 0.05em; margin-bottom: 4px;">Global Ingress URLs</div>
                ${(this._systemConfig?.shared_ingress_urls || ['https://api.crate.cc']).map((url: string) => `
                  <div style="font-family: monospace; font-size: 1rem; color: #60a5fa; margin-bottom: 4px;">${url}</div>
                `).join('')}
              </div>
            </div>
          </div>
        </div>
      ` : ''}

      ${this._hasDedicated ? `
        <div style="margin-bottom: 48px;">
          <div class="header" style="margin-bottom: 16px;">
            <div>
              <h3 style="margin: 0 0 8px 0; font-family: var(--font-main); font-weight: 500; font-size: 1.1rem; color: var(--text-main);">Dedicated Gateway Nodes</h3>
              <p style="margin: 0; color: var(--text-dim); font-size: 0.9rem;">Fully managed, isolated gateway instances deployed for your organization.</p>
            </div>
            <app-button variant="ghost" icon="external-link" onclick="window.open('mailto:support@crate.cc', '_blank')">Contact Support</app-button>
          </div>
          <div class="table-container">
            ${dedicatedNodes.length === 0 ? `
              <div style="padding: 40px; text-align: center; color: var(--text-dim);">
                <app-icon name="server" size="32" style="opacity: 0.5; margin-bottom: 16px;"></app-icon>
                <p>No dedicated nodes found. They may be provisioning.</p>
              </div>
            ` : `
              <table>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Gateway ID</th>
                    <th>Status</th>
                    <th>Last Seen</th>
                  </tr>
                </thead>
                <tbody>
                  ${dedicatedNodes.map(node => `
                    <tr class="table-row">
                      <td style="font-weight: 500; color: var(--text-main);">${node.name}</td>
                      <td>
                        <div style="display: flex; align-items: center; gap: 8px;">
                          <span style="font-family: monospace; color: var(--text-dim); font-size: 0.85rem;">${node.id}</span>
                          <button class="btn-copy" data-id="${node.id}" title="Copy ID">
                            <app-icon name="copy" size="xs"></app-icon>
                          </button>
                        </div>
                      </td>
                      <td>
                        ${!node.is_provisioned ? `
                          <span class="badge-provisioning">
                            <div class="pulse-dot"></div> Provisioning (up to 48h)
                          </span>
                        ` : `
                          <span class="${node.active ? 'badge-active' : 'badge-inactive'}">
                            ${node.active ? 'Active' : 'Offline'}
                          </span>
                        `}
                      </td>
                      <td style="color: var(--text-dim);">
                        ${node.is_provisioned && node.last_seen_at ? new Date(node.last_seen_at).toLocaleString() : 'Pending Setup'}
                      </td>
                    </tr>
                  `).join('')}
                </tbody>
              </table>
            `}
          </div>
        </div>
      ` : ''}

      ${this._hasByoc ? `
        <div style="margin-bottom: 48px;">
          <div class="header" style="margin-bottom: 16px;">
            <div>
              <h3 style="margin: 0 0 8px 0; font-family: var(--font-main); font-weight: 500; font-size: 1.1rem; color: var(--text-main);">Bring Your Own Cloud (BYOC) Nodes</h3>
              <p style="margin: 0; color: var(--text-dim); font-size: 0.9rem;">Manage your organization's self-hosted gateway instances.</p>
            </div>
            <app-button variant="secondary" size="sm" icon="plus" id="btn-provision" ${!canProvision ? 'disabled title="Maximum BYOC nodes reached. Purchase more to provision additional nodes."' : ''}>Provision Node</app-button>
          </div>

          <div class="table-container">
            ${byocNodes.length === 0 ? `
              <div style="padding: 40px; text-align: center; color: var(--text-dim);">
                <app-icon name="server" size="32" style="opacity: 0.5; margin-bottom: 16px;"></app-icon>
                <p>No BYOC nodes provisioned yet.</p>
              </div>
            ` : `
              <table>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Gateway ID</th>
                    <th>Status</th>
                    <th>Last Seen</th>
                    <th style="width: 120px;">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  ${byocNodes.map(node => `
                    <tr class="table-row">
                      <td style="font-weight: 500; color: var(--text-main);">${node.name}</td>
                      <td>
                        <div style="display: flex; align-items: center; gap: 8px;">
                          <span style="font-family: monospace; color: var(--text-dim); font-size: 0.85rem;">${node.id}</span>
                          <button class="btn-copy" data-id="${node.id}" title="Copy ID">
                            <app-icon name="copy" size="xs"></app-icon>
                          </button>
                        </div>
                      </td>
                      <td>
                        ${!node.is_provisioned ? `
                          <span class="badge-inactive" style="background: rgba(156, 163, 175, 0.1); color: #9ca3af;">
                            Pending Setup
                          </span>
                        ` : `
                          <span class="${node.active ? 'badge-active' : 'badge-inactive'}">
                            ${node.active ? 'Active' : 'Offline'}
                          </span>
                        `}
                      </td>
                      <td style="color: var(--text-dim);">
                        ${node.is_provisioned && node.last_seen_at ? new Date(node.last_seen_at).toLocaleString() : 'Pending Setup'}
                      </td>
                      <td>
                        <div style="display: flex; gap: 8px; align-items: center;">
                          <app-button variant="ghost" size="sm" class="btn-setup" data-id="${node.id}">View Setup</app-button>
                          <app-button variant="danger" size="sm" icon="trash" class="btn-revoke" data-id="${node.id}" data-name="${node.name}" title="Revoke Node"></app-button>
                        </div>
                      </td>
                    </tr>
                  `).join('')}
                </tbody>
              </table>
            `}
          </div>
        </div>
      ` : ''}
    `;

    this.shadowRoot.querySelector('#btn-provision')?.addEventListener('click', () => this.handleProvisionNode());
    
    const setupBtns = this.shadowRoot.querySelectorAll('.btn-setup');
    setupBtns.forEach(btn => {
      btn.addEventListener('click', (e) => {
        const id = (e.currentTarget as HTMLElement).getAttribute('data-id');
        if (id) {
          const node = this._nodes.find(n => n.id === id);
          if (node) this.showProvisioningModal(node);
        }
      });
    });

    const revokeBtns = this.shadowRoot.querySelectorAll('.btn-revoke');
    revokeBtns.forEach(btn => {
      btn.addEventListener('click', (e) => {
        const id = (e.currentTarget as HTMLElement).getAttribute('data-id');
        const name = (e.currentTarget as HTMLElement).getAttribute('data-name');
        if (id && name) this.handleRevokeNode(id, name);
      });
    });

    const copyBtns = this.shadowRoot.querySelectorAll('.btn-copy');
    copyBtns.forEach(btn => {
      btn.addEventListener('click', (e) => {
        const target = e.currentTarget as HTMLElement;
        const id = target.getAttribute('data-id');
        if (id) {
          this.copyToClipboard(id, target);
        }
      });
    });
  }

  private copyToClipboard(text: string, element: HTMLElement) {
    navigator.clipboard.writeText(text).then(() => {
        const originalHTML = element.innerHTML;
        element.innerHTML = '<span style="color:#10b981; font-size: 0.75rem; font-weight: 500;">Copied!</span>';
        setTimeout(() => {
            if (element.isConnected) {
                element.innerHTML = originalHTML;
            }
        }, 2000);
    });
  }
}

if (!customElements.get('gateway-nodes-tab')) {
  customElements.define('gateway-nodes-tab', GatewayNodesTab);
}
